import { CompleteEligibilitySubStepInput } from './eligibility';
import { cancelInvestment } from './state-machine';
import { Investment, InvestmentCurrency, InvestmentStage } from './types';

// v3 (2026-08-05): the mocked Payment Setup feature and its fund fields were
// removed in favor of real API payment config, so every persisted v2 fund
// blob carries now-defunct fields — a fresh seed is the migration.
const STORAGE_KEY = 'gp-investing-mock-v3';

/** Single mock investor — this prototype does not bind the mock domain to real auth. */
export const MOCK_INVESTOR_ID = 'investor-current-user';

/** Simulated ~300ms network latency for the mock fetcher functions. */
const MOCK_LATENCY_MS = 300;

interface InvestingStoreState {
  investments: Investment[];
}

/** Pure seed builder — deterministic, so two calls always deep-equal. */
const createSeedData = (): InvestingStoreState => ({
  investments: [],
});

const hasLocalStorage = (): boolean => {
  try {
    return typeof window !== 'undefined' && !!window.localStorage;
  } catch {
    return false;
  }
};

/**
 * Fallback for environments without `window.localStorage` — notably this
 * repo's vitest suite (`environment: 'node'` in `vitest.config.ts`), which
 * has no `window` at all. Without this, every `getStore()` call re-seeded
 * from scratch and no store write was ever observable by a later call in the
 * same test, which the tests worked around by never chaining a write and a
 * read. `hasLocalStorage()` still gates which path is used, so real browser
 * behavior (persist via `window.localStorage`) is unchanged.
 */
let memoryStoreFallback: InvestingStoreState | null = null;

const writeStore = (state: InvestingStoreState): void => {
  if (!hasLocalStorage()) {
    memoryStoreFallback = JSON.parse(JSON.stringify(state));
    return;
  }
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
};

const readRawStore = (): InvestingStoreState | null => {
  if (!hasLocalStorage()) {
    // Both read and write paths JSON-round-trip to ensure callers can never
    // mutate the fallback through a shared reference — mirroring localStorage's
    // serialization boundary.
    return memoryStoreFallback
      ? (JSON.parse(JSON.stringify(memoryStoreFallback)) as InvestingStoreState)
      : null;
  }

  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw) as InvestingStoreState;
  } catch {
    return null;
  }
};

/** Reads the persisted store, seeding (and persisting the seed) on first load. */
const getStore = (): InvestingStoreState => {
  const existing = readRawStore();
  if (existing) return existing;

  const seeded = createSeedData();
  writeStore(seeded);
  return seeded;
};

const updateStore = (updater: (_state: InvestingStoreState) => InvestingStoreState): void => {
  const next = updater(getStore());
  writeStore(next);
};

const simulateLatency = <T>(value: T): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), MOCK_LATENCY_MS));

const generateId = (prefix: string): string =>
  `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

// ==================== Investment stages ====================

export const TERMINAL_STAGES = new Set<string>([
  InvestmentStage.Invested,
  InvestmentStage.Rejected,
  InvestmentStage.NotEligible,
  InvestmentStage.Expired,
  InvestmentStage.Redeemed,
]);

// ==================== Investment writes ====================

export interface CreateInvestmentInput {
  fundId: string;
  investorId?: string;
  amount: number;
  currency: InvestmentCurrency;
}

/** Creates a new Investment at stage SubscriptionStarted (the tracer-bullet entry point). */
export const createInvestment = (input: CreateInvestmentInput): Promise<Investment> => {
  const now = new Date().toISOString();
  const investment: Investment = {
    id: generateId('investment'),
    fundId: input.fundId,
    investorId: input.investorId ?? MOCK_INVESTOR_ID,
    stage: InvestmentStage.SubscriptionStarted,
    amount: input.amount,
    currency: input.currency,
    paymentMethod: null,
    wireConfirmation: null,
    fundsReceived: null,
    unitsIssued: null,
    createdAt: now,
    lastActivityAt: now,
    stageTimeline: [{ stage: InvestmentStage.SubscriptionStarted, at: now }],
  };

  updateStore((state) => ({ ...state, investments: [...state.investments, investment] }));

  return simulateLatency(investment);
};

// ==================== Investor Eligibility wizard (ticket 02) ====================

export type CompleteEligibilitySubStepPayload = Pick<
  CompleteEligibilitySubStepInput,
  'subStep' | 'taxForm'
> & { investmentId: string };

// ==================== Cancel investment (ticket 06) ====================

const findInvestmentOrThrow = (investmentId: string): Investment => {
  const investment = getStore().investments.find((candidate) => candidate.id === investmentId);
  if (!investment) throw new Error(`Investment not found: ${investmentId}`);
  return investment;
};

/**
 * I/O wrapper around the pure `cancelInvestment` transition — see docs/ubiquitous-language.md
 * → "Cancel (investor)". `async` so a not-found / no-longer-cancellable throw
 * surfaces as a rejected Promise (matching every other write in this file
 * returning `Promise<T>`) rather than throwing synchronously on call. Once
 * cancelled the investment lands at stage `Rejected`, a terminal stage.
 */
export const cancelInvestmentInStore = async (investmentId: string): Promise<Investment> => {
  const nextInvestment = cancelInvestment(findInvestmentOrThrow(investmentId));

  updateStore((state) => ({
    ...state,
    investments: state.investments.map((candidate) =>
      candidate.id === nextInvestment.id ? nextInvestment : candidate,
    ),
  }));

  return simulateLatency(nextInvestment);
};
